import React, { useState, useEffect } from "react"
import { useForm, SubmitHandler } from "react-hook-form"
import axios from "axios"

import InputField from "../Inputs/InputField"
import SelectField from "../Inputs/SelectField"
import TextareaField from "../Inputs/TextareaField"
import DateField from "../Inputs/DateField"
import FileField from "../Inputs/FileField"
import CheckboxGroup from "../Inputs/CheckboxGroup"
import CountrySelect from "../Inputs/CountrySelect"

import useFlavours from "../../../hooks/useFlavours"
import { TITLE_SELECT } from "../../../utils/utils_constants"
import {
  convertBase64,
  buildAssetUpload,
  formatFormDate,
  getPlainText,
} from "../../../utils"
import { AssetUpload } from "../../../models/common"
import useDictionary from "../../../hooks/useDictionary"

interface Props {
  workingHandler?: Function
  pageContext: any
}

const defaultProps = {}

type FormValues = {
  "First Name": string
  Email: string
  "Confirm Email": string
  "Sanitarium Australia - Recipe of the Week": string[] | Boolean
  "Sanitarium Australia - Wholicious living": string[] | Boolean
  "Sanitarium Australia - Product news": string[] | Boolean
}

export const Subscribe: React.FC<Props> = props => {
  // hooks
  const {
    register,
    handleSubmit,
    trigger,
    getValues,
    setValue,
    setError,
    formState,
    control,
  } = useForm<FormValues>()

  // state
  const [working, setWorking] = useState(false)
  const [errorSubmitting, setErrorSubmitting] = useState(false)
  const [formFunctions, setFormFunctions] = useState({
    register: register,
    handleSubmit: handleSubmit,
    errors: formState.errors,
    trigger: trigger,
    getValues: getValues,
    setValue: setValue,
    control: control,
  })

  useEffect(() => {
    setFormFunctions({
      register: register,
      handleSubmit: handleSubmit,
      errors: formState.errors,
      trigger: trigger,
      getValues: getValues,
      setValue: setValue,
      control: control,
    })
  }, [
    formState.errors,
    register,
    handleSubmit,
    trigger,
    getValues,
    setValue,
    control,
  ])

  // events
  const onSubmit: SubmitHandler<FormValues> = async (data: any) => {
    // validate email field comparison
    if (data["Email"] !== data["Confirm Email"]) {
      setError("Email", {
        type: "custom",
        message: "Emails do not match",
      })
      setError("Confirm Email", {
        type: "custom",
        message: "Emails do not match",
      })
    } else {
      setWorking(true)
      if (props.workingHandler) {
        props.workingHandler(true)
      }
      setErrorSubmitting(false)
      const formData = new FormData()
      const fileKeys = ["Image 1", "Image 2", "Image 3"]

      Object.entries(data).forEach(([key, value]) => {
        if (fileKeys.includes(key)) {
          if ((value as any[])[0] != null) {
            formData.append(key, (value as any[])[0], (value as any[])[0].name)
          }
        } else {
          formData.append(key, value as any)
        }
      })

      const sfEndPoint =
        "https://mcr42t499sfpsj528-hlv5qh4smm.rest.marketingcloudapis.com/hub/v1/dataeventsasync/key:10447D13-7DAE-4947-82AA-793C29FE4969/rowset"
      const sfImageEndPoint = ""
      const sfImageCategory = ""
      const sfRecordType = "SubscribeForm"

      formData.append("sfEndPoint", sfEndPoint as any)
      formData.append("sfImageEndPoint", sfImageEndPoint as any)
      formData.append("sfRecordType", sfRecordType as any)
      formData.append("sfImageCategory", sfImageCategory as any)
      const sf_response = await axios.post("/api/form", formData)
      if (
        (sf_response.status == 200 || sf_response.status == 202) &&
        sf_response.data.success
      ) {
        document.location.href = "/au/about/subscribe/thanks"
      } else {
        setWorking(false)
        if (props.workingHandler) {
          props.workingHandler(false)
        }
        setErrorSubmitting(true)
      }
    }
  }

  return (
    <section className="form">
      <form onSubmit={handleSubmit(onSubmit)} encType="multipart/form-data">
        <div className="grid-x grid-margin-x">
          <div className="cell small-11 large-6">
            <div>&nbsp;</div>

            <InputField
              pageContext={props.pageContext}
              formFunctions={formFunctions}
              label={useDictionary("First Name", props.pageContext.locale)}
            />
            <InputField
              pageContext={props.pageContext}
              formFunctions={formFunctions}
              type="email"
              label={useDictionary("Email Address", props.pageContext.locale)}
              name="Email"
              required
              maxLength={254}
            />
            <InputField
              pageContext={props.pageContext}
              formFunctions={formFunctions}
              type="email"
              label={useDictionary("Confirm Email", props.pageContext.locale)}
              required
              maxLength={254}
            />
          </div>
          <div className="cell small-11 large-6">
            <div>
              {useDictionary(
                "Which eNewsletters would you like to subscribe to?",
                props.pageContext.locale
              )}
            </div>
            <CheckboxGroup
              pageContext={props.pageContext}
              formFunctions={formFunctions}
              name="Sanitarium Australia - Recipe of the Week"
              label={useDictionary(
                "Sanitarium Australia - Recipe of the Week",
                props.pageContext.locale
              )}
              hideLabel
              options={[
                {
                  value: "yes",
                  label: useDictionary(
                    "Recipe of the Week",
                    props.pageContext.locale
                  ),
                },
              ]}
            />
            <CheckboxGroup
              pageContext={props.pageContext}
              formFunctions={formFunctions}
              name="Sanitarium Australia - Wholicious living"
              label={useDictionary(
                "Sanitarium Australia - Wholicious living",
                props.pageContext.locale
              )}
              hideLabel
              options={[
                {
                  value: "yes",
                  label: useDictionary(
                    "Wholicious living",
                    props.pageContext.locale
                  ),
                },
              ]}
            />
            <CheckboxGroup
              pageContext={props.pageContext}
              formFunctions={formFunctions}
              name="Sanitarium Australia - Product news"
              label={useDictionary(
                "Sanitarium Australia - Product news",
                props.pageContext.locale
              )}
              hideLabel
              options={[
                {
                  value: "yes",
                  label: useDictionary(
                    "Product news",
                    props.pageContext.locale
                  ),
                },
              ]}
            />
          </div>
        </div>

        <div className="form__cta">
          <input
            type="submit"
            className="button"
            value={
              working
                ? useDictionary("Please wait", props.pageContext.locale)
                : useDictionary("Send details", props.pageContext.locale)
            }
            disabled={working}
          />
        </div>
        {errorSubmitting && (
          <>
            <p className="error">
              {useDictionary(
                "Something went wrong, please try again.",
                props.pageContext.locale
              )}
            </p>
          </>
        )}
      </form>
    </section>
  )
}

Subscribe.defaultProps = defaultProps

export default Subscribe
